import { useEffect, useMemo, useState } from 'react';

import { useAgentId, useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { operatorsBackrefRepoFor } from '../lib/repositories/earnings-repo.js';
import {
  callAiDraftPitch,
  callAiAgentPriorityInsight,
  type AiPriorityInsightItem,
} from '../lib/callables/ai.js';
import {
  DEFAULT_SORT_KEY,
  DEFAULT_STATUS_FILTER,
  PRIORITY_REASON_LABEL_KEYS,
  SORT_OPTIONS,
  STATUS_FILTERS,
  applyOperatorsView,
  countsByStatus,
  isSortKey,
  operatorPriorityReason,
  rankOperators,
  tsToMs,
  type PriorityReason,
  type SortKey,
  type StatusFilter,
} from '../lib/operators-view.js';
import { celebrateOnce, feel, Skeleton as UiSkeleton } from '@kilat/ui';
import { parseRateLimitError, phone as phoneUtil, type AgentOperatorBackref } from '@kilat/shared';

/** Radius tokens, hoisted out of JSX so `no-literal-string` sees no literal. */
const RADIUS_MD = 'var(--radius-md)';

// Decorative AI glyph (aria-hidden at every use site). A module const so the
// icon isn't a bare JSX string literal (which i18n lint would otherwise flag).
const SPARKLE_ICON = '✦';

/**
 * /operators — every tenant the agent brought in, with status pill,
 * signup date, first-paid date, and a one-tap WhatsApp deep-link
 * carrying a templated message tuned to the operator's current state
 * (welcome / nudge to first ticket / past-due settle reminder).
 *
 * Reads `agents/{agentId}/operators/{tenantId}` back-refs which
 * `tenantBootstrap` writes at signup and `commissionAccrue` patches on
 * each paid invoice (Sprint 40 enriched the schema with name + phone +
 * email + planIdAtSignup so we can render without a cross-tenant read).
 *
 * Per [feedback-no-dead-ends]: every state lands in a clear next action.
 *   - signed-out / not-an-agent / loading / error / empty → explicit copy + CTA
 *   - populated → grouped by status with sensible ordering
 *
 * The default "Priority" sort surfaces who needs attention first: past-due
 * (churn / commission at stake) and trial (convert before the 14-day expiry)
 * lead; healthy active operators sit lowest. Each row shows a reason chip so
 * the order is explained, not magic. See `rankOperators` for the rule.
 */
export function OperatorsPage() {
  const { user, loading: authLoading } = useAuth();
  const agentId = useAgentId();
  const t = useI18n();
  const brand = usePlatformBrand();
  // Listener-level failure + retry nonce. Without onError a denied/dropped read
  // left `listLoading` stuck true forever and the error branch below was
  // unreachable — a permanent skeleton with no escape ([[feedback-no-dead-ends]]).
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  // Client-only view controls over the already-loaded back-refs — no new
  // listener, no new read. Default: everyone, priority (smart) order first.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(DEFAULT_STATUS_FILTER);
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);

  const subscribe = useMemo(() => {
    if (!agentId) return null;
    const repo = operatorsBackrefRepoFor(agentId);
    return (cb: (vs: AgentOperatorBackref[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
  }, [agentId]);

  const { data: backrefs, loading: listLoading } = useTrackedSnapshot<AgentOperatorBackref[]>(
    ['operators', agentId, retryNonce],
    subscribe,
  );

  // Dopamine: confetti the agent's first operator signup. Keyed by
  // agentId so each fresh agent gets their celebration; localStorage
  // single-fire guard inside celebrateOnce. Fires the moment the live
  // backref list flips from empty → non-empty.
  useEffect(() => {
    if (!agentId || !backrefs || backrefs.length === 0) return;
    void celebrateOnce({
      key: `firstSignup:${agentId}`,
      intensity: 'milestone',
    }).then((fired) => {
      if (fired) feel('celebration');
    });
  }, [agentId, backrefs]);

  // ── states ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );
  }
  if (!user) {
    return (
      <Shell>
        <EmptyCard
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.operators.signedOutBody')}
        />
      </Shell>
    );
  }
  if (!agentId) {
    return (
      <Shell>
        <EmptyCard
          title={t.t('agent.payouts.notAgentTitle')}
          body={t.t('agent.operators.notAgentBody')}
          ctaLabel={t.t('agent.payouts.notAgentCta')}
          ctaHref={`https://${brand.domains.marketing}/become-an-agent`}
        />
      </Shell>
    );
  }
  if (loadError) {
    return (
      <Shell>
        <EmptyCard
          title={t.t('agent.loadError.operatorsTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </Shell>
    );
  }
  if (listLoading || backrefs === null) {
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );
  }
  if (backrefs.length === 0) {
    return (
      <Shell>
        <EmptyCard
          title={t.t('agent.operators.emptyTitle')}
          body={t.t('agent.operators.emptyBody')}
          ctaLabel={t.t('agent.map.openCard')}
          ctaHref="/code"
        />
      </Shell>
    );
  }

  // ── populated ───────────────────────────────────────────────────────
  // Filter + sort are pure re-shapes of the already-loaded list — no new
  // listener, no new read (out-of-scope: outlet count + earnings sort need
  // backend data this slice deliberately defers).
  const nowMs = Date.now();
  const counts = countsByStatus(backrefs);
  const visible = applyOperatorsView(backrefs, { statusFilter, sortKey, nowMs });
  // Genuine priority top for the AI focus card — computed from the FULL list,
  // independent of the current sort/filter, so the narrative always reasons
  // about real priority (business name + status + coarse days only, no PII).
  const focusTop = rankOperators(backrefs, nowMs)
    .slice(0, 4)
    .map((b) => toInsightItem(b, nowMs));
  return (
    <Shell>
      <header style={{ marginBottom: 'var(--space-2)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {t.t('agent.operators.title')}
        </h1>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('agent.operators.filter.activeCount', {
            shown: visible.length,
            total: backrefs.length,
          })}
        </p>
      </header>

      <AiFocusCard agentId={agentId} top={focusTop} />

      <FilterSortBar
        statusFilter={statusFilter}
        sortKey={sortKey}
        counts={counts}
        onStatusChange={setStatusFilter}
        onSortChange={setSortKey}
      />

      {visible.length === 0 ? (
        <section
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            textAlign: 'center',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          <p
            style={{
              margin: '0 auto',
              maxWidth: 420,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {t.t('agent.operators.noneForFilter')}
          </p>
          <button
            type="button"
            onClick={() => setStatusFilter(DEFAULT_STATUS_FILTER)}
            style={{
              ...ctaStyle,
              marginTop: 'var(--space-4)',
              minHeight: 'var(--touch-target, 44px)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t.t('agent.operators.filter.all')}
          </button>
        </section>
      ) : (
        <section
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          <ul
            className="ag-grid"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {visible.map((b) => (
              <OperatorRow
                key={b.tenantId ?? b.id ?? b.tenantBusinessName}
                backref={b}
                agentId={agentId}
                nowMs={nowMs}
              />
            ))}
          </ul>
        </section>
      )}
    </Shell>
  );
}

// ── filter + sort controls ───────────────────────────────────────────────

function FilterSortBar({
  statusFilter,
  sortKey,
  counts,
  onStatusChange,
  onSortChange,
}: {
  readonly statusFilter: StatusFilter;
  readonly sortKey: SortKey;
  readonly counts: Record<StatusFilter, number>;
  readonly onStatusChange: (v: StatusFilter) => void;
  readonly onSortChange: (v: SortKey) => void;
}) {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-3) var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <span style={controlLabelStyle}>{t.t('agent.operators.filter.label')}</span>
        <div
          role="group"
          aria-label={t.t('agent.operators.filter.label')}
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            overflowX: 'auto',
            paddingBottom: 2,
            scrollbarWidth: 'none',
          }}
        >
          {STATUS_FILTERS.map((f) => {
            const selected = f.value === statusFilter;
            return (
              <button
                key={f.value}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  if (!selected) feel('tap');
                  onStatusChange(f.value);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  padding: 'var(--space-2) var(--space-3)',
                  minHeight: 'var(--touch-target, 44px)',
                  borderRadius: 'var(--radius-pill)',
                  border: selected
                    ? '1px solid var(--color-primary)'
                    : '1px solid var(--color-border-strong)',
                  background: selected ? 'var(--color-primary)' : 'transparent',
                  color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
                  fontSize: 'var(--text-meta)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t.t(f.labelKey)}
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    padding: '1px 7px',
                    borderRadius: 'var(--radius-pill)',
                    background: selected ? 'rgba(255,255,255,0.22)' : 'var(--color-border)',
                    color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface-muted)',
                  }}
                >
                  {counts[f.value]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <span style={controlLabelStyle}>{t.t('agent.operators.sort.label')}</span>
        <select
          value={sortKey}
          onChange={(e) => {
            const next = e.target.value;
            if (isSortKey(next)) {
              feel('tap');
              onSortChange(next);
            }
          }}
          style={{
            appearance: 'none',
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            minHeight: 'var(--touch-target, 44px)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border-strong)',
            background: 'var(--color-surface)',
            color: 'var(--color-on-surface)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {t.t(o.labelKey)}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
}

const controlLabelStyle: React.CSSProperties = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};

// ── row ─────────────────────────────────────────────────────────────────

function OperatorRow({
  backref,
  agentId,
  nowMs,
}: {
  readonly backref: AgentOperatorBackref;
  readonly agentId: string;
  readonly nowMs: number;
}) {
  const t = useI18n();
  const name = backref.tenantBusinessName ?? backref.tenantId ?? 'Operator';
  const status: AgentOperatorBackref['currentStatus'] = backref.currentStatus ?? 'trial';
  const signupMs = tsToMs(backref.signupAt);
  const firstPaidMs = backref.firstPaidAt ? tsToMs(backref.firstPaidAt) : 0;
  const lastPaidMs = backref.lastPaidAt ? tsToMs(backref.lastPaidAt) : 0;
  const phone = backref.tenantContactPhone;
  const waLink = buildWhatsAppLink(phone, name, status);

  // Days-since context for aiDraftPitch (only when we have the dates). Uses the
  // page's shared `nowMs` prop so a row's reason chip matches the ranking snapshot.
  const daysSinceSignup =
    signupMs > 0 ? Math.max(0, Math.floor((nowMs - signupMs) / 86_400_000)) : undefined;
  const daysSinceLastPaid =
    lastPaidMs > 0 ? Math.max(0, Math.floor((nowMs - lastPaidMs) / 86_400_000)) : undefined;
  // The rule-based reason this operator sits where it does — a chip so the
  // priority order is explained, not magic ([[feedback-no-dead-ends]]).
  const reason = operatorPriorityReason(backref, nowMs);

  return (
    <li
      style={{
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--tint-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        <span
          style={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </span>
        <StatusPill status={status} />
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {t.t('agent.operators.signedUp')} {formatRelative(signupMs)}
        {firstPaidMs > 0
          ? ` · ${t.t('agent.operators.firstPaid', { date: formatDate(firstPaidMs) })}`
          : ` · ${t.t('agent.operators.notPaidYet')}`}
        {lastPaidMs > 0 && lastPaidMs !== firstPaidMs
          ? ` · ${t.t('agent.operators.lastPaid', { date: formatDate(lastPaidMs) })}`
          : ''}
      </p>
      <ReasonChip reason={reason} />
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {waLink ? (
          <a href={waLink} target="_blank" rel="noopener noreferrer" style={ctaStyle}>
            {t.t('agent.operators.whatsapp')}
          </a>
        ) : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            {t.t('agent.operators.phoneNotOnFile')}
          </span>
        )}
        <PitchWithAi
          agentId={agentId}
          operatorName={name}
          status={status}
          phone={phone}
          daysSinceSignup={daysSinceSignup}
          daysSinceLastPaid={daysSinceLastPaid}
        />
      </div>
    </li>
  );
}

/**
 * Inline AI pitch composer for a single operator. Tap → drafts a
 * status-aware WhatsApp message via `aiDraftPitch` → renders preview
 * → "Send via WhatsApp" deep-links with the AI text pre-filled.
 *
 * Six states explicit per [[feedback-no-dead-ends]]:
 *   - idle (button only)
 *   - drafting (spinner + disable)
 *   - suggested (preview + Send / Try again)
 *   - noop (orange explainer pointing super-admin at the key)
 *   - rate_limited (countdown copy)
 *   - failed (recovery copy)
 */
function PitchWithAi({
  agentId,
  operatorName,
  status,
  phone,
  daysSinceSignup,
  daysSinceLastPaid,
}: {
  readonly agentId: string;
  readonly operatorName: string;
  readonly status: NonNullable<AgentOperatorBackref['currentStatus']>;
  readonly phone: string | undefined;
  readonly daysSinceSignup: number | undefined;
  readonly daysSinceLastPaid: number | undefined;
}) {
  const t = useI18n();
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'drafting' }
    | { kind: 'suggested'; text: string; cacheHit: boolean }
    | { kind: 'noop' }
    | { kind: 'failed'; message: string; retryAfterSec?: number }
  >({ kind: 'idle' });

  async function draftIt() {
    feel('tap');
    setStage({ kind: 'drafting' });
    try {
      const out = await callAiDraftPitch({
        agentId,
        operatorName,
        status,
        ...(typeof daysSinceSignup === 'number' ? { daysSinceSignup } : {}),
        ...(typeof daysSinceLastPaid === 'number' ? { daysSinceLastPaid } : {}),
      });
      if (out.provider === 'noop') {
        setStage({ kind: 'noop' });
        return;
      }
      setStage({ kind: 'suggested', text: out.text, cacheHit: out.cacheHit });
    } catch (err) {
      const rl = parseRateLimitError(err);
      if (rl) {
        setStage({ kind: 'failed', message: rl.message, retryAfterSec: rl.retryAfterSec });
        return;
      }
      setStage({ kind: 'failed', message: (err as Error).message ?? 'Could not draft.' });
    }
  }

  function sendViaWhatsapp(text: string) {
    if (!phone) return;
    const normalised = phoneUtil.normaliseMalaysianPhone(phone);
    if (!normalised) return;
    feel('tap');
    const digits = normalised.replace(/^\+/, '');
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  if (stage.kind === 'idle' || stage.kind === 'drafting') {
    return (
      <button
        type="button"
        onClick={() => void draftIt()}
        disabled={stage.kind === 'drafting'}
        style={ghostCtaStyle}
      >
        <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>
          {SPARKLE_ICON}
        </span>
        {stage.kind === 'drafting'
          ? t.t('agent.operators.drafting')
          : t.t('agent.operators.pitchWithAi')}
      </button>
    );
  }

  if (stage.kind === 'noop') {
    return (
      <PitchPanel onClose={() => setStage({ kind: 'idle' })}>
        <p style={pitchNotice}>
          <strong>{t.t('agent.operators.aiOffTitle')}</strong> {t.t('agent.operators.aiOffBody')}
        </p>
      </PitchPanel>
    );
  }

  if (stage.kind === 'failed') {
    return (
      <PitchPanel onClose={() => setStage({ kind: 'idle' })}>
        <p
          style={{
            ...pitchNotice,
            background: 'rgba(239,68,68,0.06)',
            borderColor: 'rgba(239,68,68,0.20)',
            color: '#991b1b',
          }}
        >
          {stage.message}
          {stage.retryAfterSec ? (
            <span style={{ display: 'block', marginTop: 4 }}>
              {t.t('agent.operators.tryAgainIn', { n: stage.retryAfterSec })}
            </span>
          ) : null}
        </p>
      </PitchPanel>
    );
  }

  // suggested
  const canSend = Boolean(phone && phoneUtil.normaliseMalaysianPhone(phone));
  return (
    <PitchPanel onClose={() => setStage({ kind: 'idle' })}>
      <div style={pitchPreview}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={pitchPreviewLabel}>{t.t('agent.operators.suggestedMessage')}</span>
          {stage.cacheHit ? (
            <span
              style={{
                fontSize: 9,
                padding: '2px 6px',
                borderRadius: 999,
                background: 'rgba(13,122,79,0.10)',
                color: 'var(--color-primary)',
                fontWeight: 600,
                letterSpacing: 0.3,
              }}
              title={t.t('agent.operators.cachedTitle')}
            >
              CACHED
            </span>
          ) : null}
        </div>
        <p style={pitchPreviewText}>{stage.text}</p>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {canSend ? (
          <button type="button" onClick={() => sendViaWhatsapp(stage.text)} style={ctaStyle}>
            {t.t('agent.operators.sendViaWhatsapp')}
          </button>
        ) : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            {t.t('agent.operators.phoneNotOnFileCopy')}
          </span>
        )}
        <button type="button" onClick={() => void draftIt()} style={ghostCtaStyle}>
          {t.t('agent.operators.tryAnother')}
        </button>
      </div>
    </PitchPanel>
  );
}

function PitchPanel({
  children,
  onClose,
}: {
  readonly children: React.ReactNode;
  readonly onClose: () => void;
}) {
  const t = useI18n();
  return (
    <div
      style={{
        width: '100%',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(13,122,79,0.04)',
        border: '1px solid rgba(13,122,79,0.18)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 22,
            height: 22,
            borderRadius: 6,
            background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
            color: '#ffffff',
            fontSize: 11,
          }}
        >
          {SPARKLE_ICON}
        </span>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t('agent.operators.aiPitch')}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t.t('agent.operators.close')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-on-surface-muted)',
            fontSize: 16,
            lineHeight: 1,
            padding: 2,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

const ghostCtaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: 'var(--space-2) var(--space-4)',
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(13,122,79,0.04)',
  color: 'var(--color-primary)',
  border: '1px solid rgba(13,122,79,0.22)',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const pitchNotice: React.CSSProperties = {
  margin: 0,
  padding: '8px 12px',
  borderRadius: 8,
  background: 'rgba(245,158,11,0.06)',
  border: '1px solid rgba(245,158,11,0.22)',
  color: '#b45309',
  fontSize: 'var(--text-meta)',
  lineHeight: 1.5,
};

const pitchPreview: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 'var(--space-2) var(--space-3)',
};

const pitchPreviewLabel: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 600,
};

const pitchPreviewText: React.CSSProperties = {
  margin: '6px 0 0 0',
  fontSize: 13,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  color: 'var(--color-on-surface)',
};

const ctaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-2) var(--space-4)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
};

function StatusPill({ status }: { readonly status: AgentOperatorBackref['currentStatus'] }) {
  const t = useI18n();
  // Wording comes from `agent.map.status.*`, which already carries these exact
  // union values in all four locales for the agent map — so the pill and the
  // map pin describe an operator with the same word instead of two.
  const visual = (() => {
    switch (status) {
      case 'active':
        return {
          bg: 'rgba(13,122,79,0.12)',
          fg: 'var(--color-primary)',
          label: t.t('agent.map.status.active'),
        };
      case 'trial':
        return {
          bg: 'rgba(59,130,246,0.12)',
          fg: '#1d4ed8',
          label: t.t('agent.map.status.trial'),
        };
      case 'past_due':
        return {
          bg: 'rgba(245,158,11,0.12)',
          fg: '#b45309',
          label: t.t('agent.map.status.past_due'),
        };
      case 'suspended':
        return {
          bg: 'rgba(239,68,68,0.12)',
          fg: '#b45309',
          label: t.t('agent.map.status.suspended'),
        };
      case 'archived':
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t.t('agent.map.status.archived'),
        };
      default:
        return {
          bg: 'var(--color-border)',
          fg: 'var(--color-on-surface-muted)',
          label: t.t('agent.map.status.unknown'),
        };
    }
  })();
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        background: visual.bg,
        color: visual.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {visual.label}
    </span>
  );
}

// ── priority reason chip ─────────────────────────────────────────────────

/** Per-reason tint. Urgent reasons run warm; healthy/dormant stay muted. */
const REASON_VISUAL: Record<PriorityReason, { bg: string; fg: string }> = {
  pastDue: { bg: 'rgba(239,68,68,0.10)', fg: '#b91c1c' },
  trialEndingSoon: { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' },
  trialActive: { bg: 'rgba(59,130,246,0.10)', fg: '#1d4ed8' },
  winBack: { bg: 'rgba(239,68,68,0.08)', fg: '#b45309' },
  dormant: { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' },
  active: { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)' },
};

function ReasonChip({ reason }: { readonly reason: PriorityReason }) {
  const t = useI18n();
  const v = REASON_VISUAL[reason];
  return (
    <span
      style={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: 'var(--radius-pill)',
        background: v.bg,
        color: v.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
      }}
    >
      <span aria-hidden style={{ width: 5, height: 5, borderRadius: '50%', background: v.fg }} />
      {t.t(PRIORITY_REASON_LABEL_KEYS[reason])}
    </span>
  );
}

// ── AI focus card (thin optional narrative over the rule-ranked list) ─────

/**
 * Build the privacy-safe AI input for one operator: ONLY business name +
 * lifecycle status + coarse day-counts. No phone, email, plate, or customer
 * data ever crosses into this payload.
 */
function toInsightItem(backref: AgentOperatorBackref, nowMs: number): AiPriorityInsightItem {
  const signupMs = tsToMs(backref.signupAt);
  const lastPaidMs = backref.lastPaidAt ? tsToMs(backref.lastPaidAt) : 0;
  const daysSinceSignup =
    signupMs > 0 ? Math.max(0, Math.floor((nowMs - signupMs) / 86_400_000)) : undefined;
  const daysSinceLastPaid =
    lastPaidMs > 0 ? Math.max(0, Math.floor((nowMs - lastPaidMs) / 86_400_000)) : undefined;
  return {
    operatorName: backref.tenantBusinessName ?? backref.tenantId ?? 'Operator',
    status: backref.currentStatus ?? 'trial',
    ...(typeof daysSinceSignup === 'number' ? { daysSinceSignup } : {}),
    ...(typeof daysSinceLastPaid === 'number' ? { daysSinceLastPaid } : {}),
  };
}

/**
 * "AI focus" card — taps the optional `aiAgentPriorityInsight` callable to turn
 * the top rule-ranked operators into a 2–3 line "focus on these first, and why".
 * On no-op (no Groq key) / null / error it collapses to a muted note pointing
 * back at the already-ranked list — NEVER a dead error ([[feedback-no-dead-ends]]).
 */
function AiFocusCard({
  agentId,
  top,
}: {
  readonly agentId: string;
  readonly top: readonly AiPriorityInsightItem[];
}) {
  const t = useI18n();
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; narrative: string }
    | { kind: 'off'; message: string }
  >({ kind: 'idle' });

  if (top.length === 0) return null;

  async function run() {
    feel('tap');
    setStage({ kind: 'loading' });
    try {
      const out = await callAiAgentPriorityInsight({ agentId, top: [...top] });
      if (!out.narrative) {
        setStage({ kind: 'off', message: t.t('agent.operators.priority.ai.offNote') });
        return;
      }
      setStage({ kind: 'ready', narrative: out.narrative });
    } catch (err) {
      const rl = parseRateLimitError(err);
      setStage({
        kind: 'off',
        message: rl ? rl.message : t.t('agent.operators.priority.ai.offNote'),
      });
    }
  }

  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-3) var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 22,
            height: 22,
            borderRadius: 6,
            background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
            color: '#ffffff',
            fontSize: 11,
          }}
        >
          {SPARKLE_ICON}
        </span>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t('agent.operators.priority.ai.badge')}
        </span>
        {stage.kind === 'ready' || stage.kind === 'off' ? (
          <button
            type="button"
            onClick={() => setStage({ kind: 'idle' })}
            aria-label={t.t('agent.operators.priority.ai.dismiss')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-on-surface-muted)',
              fontSize: 16,
              lineHeight: 1,
              padding: 2,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {stage.kind === 'idle' ? (
        <>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {t.t('agent.operators.priority.ai.subtitle')}
          </p>
          <button
            type="button"
            onClick={() => void run()}
            style={{ ...ghostCtaStyle, alignSelf: 'flex-start' }}
          >
            <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>
              {SPARKLE_ICON}
            </span>
            {t.t('agent.operators.priority.ai.cta')}
          </button>
        </>
      ) : null}

      {stage.kind === 'loading' ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('agent.operators.priority.ai.loading')}
        </p>
      ) : null}

      {stage.kind === 'ready' ? (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            color: 'var(--color-on-surface)',
          }}
        >
          {stage.narrative}
        </p>
      ) : null}

      {stage.kind === 'off' ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {stage.message}
        </p>
      ) : null}
    </section>
  );
}

// ── shell + empty / skeleton ───────────────────────────────────────────

function Shell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

function EmptyCard({
  title,
  body,
  ctaLabel,
  ctaHref,
  onRetry,
  retryLabel,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            ...ctaStyle,
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target, 44px)',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {retryLabel}
        </button>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a href={ctaHref} style={{ ...ctaStyle, marginTop: 'var(--space-4)' }}>
          {ctaLabel}
        </a>
      ) : null}
    </section>
  );
}

function Skeleton() {
  // Shared shimmer from @kilat/ui, not a hand-rolled grey block.
  //
  // This app carried three separate local copies of this component, each
  // painting a static `var(--color-border)` rectangle — so its loading states
  // never shimmered, and [[feedback-unified-packages-ui]] ("extract before the
  // second copy lands") had been breached twice over in one app.
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      aria-busy="true"
    >
      <UiSkeleton count={3} height={96} radius={RADIUS_MD} />
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

function buildWhatsAppLink(
  phone: string | undefined,
  name: string,
  status: AgentOperatorBackref['currentStatus'],
): string | null {
  if (!phone) return null;
  const normalised = phoneUtil.toWaMeDigits(phone);
  if (!normalised) return null;
  const body = templateFor(status, name);
  return `https://wa.me/${normalised}?text=${encodeURIComponent(body)}`;
}

function templateFor(status: AgentOperatorBackref['currentStatus'], name: string): string {
  switch (status) {
    case 'trial':
      return `Hi ${name}! Just checking in — anything you'd like a hand with on Kilat? Happy to walk through the queue board or set up your first promo.`;
    case 'past_due':
      return `Hi ${name} — quick nudge: your Kilat invoice is past due. Let me know if the payment link bounced and I'll re-send you a fresh one.`;
    case 'suspended':
      return `Hi ${name} — your Kilat account is suspended. Want to get you back online today? Reply and I'll get the next steps to you.`;
    case 'archived':
      return `Hi ${name} — would love to hear what we could do differently to win you back to Kilat.`;
    case 'active':
    default:
      return `Hi ${name}! Hope business is good. Anything I can help with on Kilat — promo ideas, second outlet, staff training?`;
  }
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function formatRelative(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return formatDate(ms);
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  const days = Math.floor(diff / day);
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}
